/**
 * Regression repair, on the page you already have open.
 *
 * Sections regress between runs: the engine rewrites a page from scratch every
 * time, so a good trust band can come back worse tomorrow with nothing wrong
 * anywhere. The repair used to mean regenerating the whole page and hoping,
 * which costs a run and risks the bands that were fine.
 *
 * This swaps ONE band, slot for slot, for the same band from the same client's
 * earlier run, with pinned favorites sorted to the top, and renders the result
 * through the scoping fix. It lives beside the page it repairs because that is
 * where the problem is noticed - you are looking at the page, you see the band
 * came back worse, you fix it there.
 *
 * ⚠ ONE CLIENT. A band carries its client's city, phone, claims and form
 * shortcode with it, and the scoping fix makes a foreign band render perfectly
 * - which is the danger, not the feature. Candidates come from this tenant's
 * drafts only, `applyBandSnapshot` refuses a cross-tenant snapshot outright,
 * and a favorite pinned from another client is counted on screen and never
 * offered. See fe-reports/fav-rewrap-proof-01.md §3.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Check, Loader2, Pin, PinOff, RefreshCw } from 'lucide-react';

import {
  toPreviewDocument,
  withPlaceholderHostedForm,
} from '../previewShortcodes';
import { buildCandidates, candidateNamespace, type DraftSource } from './bandCandidates';
import { readSpine, strayBands } from './bandSpine';
import {
  applyBandSnapshot,
  listBands,
  snapshotBand,
  type BandSnapshot,
  type SnapshotFailure,
} from './scopeBand';
import {
  landingPagesService,
  type LandingDraftFull,
  type LandingDraftSummary,
} from '@/services/landingPagesService';
import { sectionFavoritesService, type SectionFavorite } from '@/services/sectionFavoritesService';

const CANDIDATE_PAGE = 8;

function flattenDrafts(history: { groups?: unknown } | undefined): LandingDraftSummary[] {
  const groups = (history?.groups ?? []) as Array<{ dates?: Array<{ runs?: LandingDraftSummary[] }> }>;
  return groups.flatMap((group) => (group.dates ?? []).flatMap((date) => date.runs ?? []));
}

function draftLabel(draft: LandingDraftSummary): string {
  const when = draft.created_at?.slice(0, 16).replace('T', ' ') ?? draft.created_date;
  return `${when} · ${draft.service_name || 'untitled'}${draft.template_slug ? ` · ${draft.template_slug}` : ''}`;
}

/** A refusal from `snapshotBand`/`applyBandSnapshot`: the reason, plus what differed when known. */
function refusal(r: SnapshotFailure): { reason: string; detail?: string } {
  return { reason: r.reason, detail: r.detail };
}

/**
 * Whether the selected candidate can actually be swapped into the page on
 * screen. Readiness follows COMPATIBILITY, not just band presence: the source
 * is snapshotted and the swap is dry-run applied. Only a swap that would
 * succeed is offered; anything else is refused with its reason.
 */
type SwapCheck =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'refused'; reason: string; detail?: string }
  // `composed` is the dry-run's full page with the candidate swapped in, so the
  // preview shows the band in real document context (fonts, wrappers, page CSS)
  // rather than a stripped pane.
  | { status: 'ready'; snapshot: BandSnapshot; composed: string };

/**
 * Things that are true about the swapped page and would otherwise be invisible.
 * A band that renders as nothing must say so; a page that now carries two form
 * slots or two H1s trips a fatal publish gate and must say so here rather than
 * at publish time.
 */
function swapWarnings(bandHtml: string, pageHtml: string): string[] {
  const out: string[] = [];
  if (/<section\b[^>]*\bhidden\b/i.test(bandHtml)) {
    out.push('this band carries a hidden attribute, so it will render zero pixels tall');
  }
  // ⚠ COUNT ELEMENTS, NOT THE STRING. Pages carry `[data-landing-form-slot]`
  // inside their own stylesheet - one real page mentions it 22 times in CSS
  // attribute selectors and carries zero actual slots. Counting raw
  // occurrences fired this warning on every swap of that page, which is worse
  // than not warning at all: an alarm that always sounds is one nobody reads.
  const slots = (pageHtml.match(/<[a-zA-Z][^>]*\bdata-landing-form-slot\b/gi) ?? []).length;
  if (slots > 1) out.push(`the page now has ${slots} form slots — the publish gate refuses more than one`);
  const h1s = (pageHtml.match(/<h1\b/gi) ?? []).length;
  if (h1s > 1) out.push(`the page now has ${h1s} H1s — the publish gate refuses more than one`);
  return out;
}

/** One side of the before/after comparison: a full page document in an iframe. */
function FullPreview({ title, html }: { title: string; html: string }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      <iframe
        title={title}
        sandbox="allow-scripts"
        srcDoc={toPreviewDocument(withPlaceholderHostedForm(html))}
        className="h-[360px] w-full rounded border bg-white"
      />
    </div>
  );
}

export default function BandSwapPanel({
  tenantId,
  pageHtml,
  copyMeta,
  currentDraftId,
}: {
  tenantId: string;
  pageHtml: string;
  copyMeta?: Record<string, unknown> | null;
  /** The run on screen, so it is never offered as a candidate against itself. */
  currentDraftId: string | null;
}) {
  const [slot, setSlot] = useState<string | null>(null);
  const [candidateDraftId, setCandidateDraftId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<Record<string, LandingDraftFull>>({});
  const [loadErrors, setLoadErrors] = useState<Record<string, string>>({});
  const [shownCount, setShownCount] = useState(CANDIDATE_PAGE);
  const [swapped, setSwapped] = useState<{ html: string; warnings: string[] } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; value: string } | null>(null);

  const history = useQuery({
    queryKey: ['band-swap', 'drafts', tenantId],
    queryFn: () => landingPagesService.listDrafts(tenantId),
    enabled: Boolean(tenantId),
  });
  const favorites = useQuery({
    queryKey: ['band-swap', 'favorites'],
    queryFn: () => sectionFavoritesService.list(),
  });

  // Nothing on screen may belong to a slot or a candidate the operator has
  // moved off. The reset happens in the handler that moves them, not in an
  // effect watching the value afterwards - an effect would render the stale
  // pane once before clearing it. The page underneath is handled by the
  // caller, which keys this panel on the run it is showing, so a different
  // page gets a fresh panel rather than one that has to clean itself up.
  const chooseSlot = (bandId: string | null) => {
    setSlot(bandId);
    setCandidateDraftId(null);
    setSwapped(null);
    setShownCount(CANDIDATE_PAGE);
  };
  const chooseCandidate = (draftId: string) => {
    setCandidateDraftId(draftId);
    setSwapped(null);
  };

  const spine = useMemo(
    () => readSpine({ presentBandIds: listBands(pageHtml), copyMeta: copyMeta ?? null }),
    [pageHtml, copyMeta],
  );
  const strays = useMemo(() => strayBands(listBands(pageHtml)), [pageHtml]);

  const drafts = useMemo(() => flattenDrafts(history.data), [history.data]);
  const others = useMemo(
    () => drafts.filter((d) => d.id !== currentDraftId),
    [drafts, currentDraftId],
  );
  const visible = useMemo(() => others.slice(0, shownCount), [others, shownCount]);

  // Load the pages behind the candidates on screen, newest first, a page at a
  // time. A draft is ~150KB and there can be 200 of them; fetching them all to
  // fill a list nobody opened is not a kindness.
  useEffect(() => {
    if (!slot) return;
    let live = true;
    for (const draft of visible) {
      if (loaded[draft.id] || loadErrors[draft.id]) continue;
      landingPagesService
        .getDraft(tenantId, draft.id)
        .then((full) => { if (live) setLoaded((prev) => ({ ...prev, [draft.id]: full })); })
        .catch((err: unknown) => {
          if (!live) return;
          const message = err instanceof Error ? err.message : String(err);
          setLoadErrors((prev) => ({ ...prev, [draft.id]: `this run could not be loaded: ${message}` }));
        });
    }
    return () => { live = false; };
  }, [slot, visible, tenantId, loaded, loadErrors]);

  const sources = useMemo((): DraftSource[] => visible.map((draft) => ({
    draftId: draft.id,
    createdAt: draft.created_at,
    label: draftLabel(draft),
    html: loaded[draft.id]?.html ?? null,
    error: loadErrors[draft.id] ?? null,
  })), [visible, loaded, loadErrors]);

  const candidateList = useMemo(() => slot
    ? buildCandidates({
        bandId: slot,
        tenantId,
        destinationHtml: pageHtml,
        drafts: sources,
        favorites: (favorites.data ?? []) as SectionFavorite[],
      })
    : null, [slot, tenantId, pageHtml, sources, favorites.data]);

  const namespace = useMemo(
    () => candidateNamespace(candidateDraftId ?? 'x', slot ?? 'slot'),
    [candidateDraftId, slot],
  );

  // Retain the selected candidate's composition for preview and Swap. The list
  // separately dry-runs its loaded candidates to show refusals before selection.
  const swapCheck = useMemo((): SwapCheck => {
    if (!slot || !candidateDraftId) return { status: 'idle' };
    const doc = loaded[candidateDraftId]?.html;
    if (!doc) return { status: 'loading' };
    const snap = snapshotBand({ doc, bandId: slot, tenantId, namespace });
    if (!snap.ok) return { status: 'refused', ...refusal(snap) };
    const dry = applyBandSnapshot({ doc: pageHtml, bandId: slot, tenantId, snapshot: snap.snapshot });
    if (!dry.ok) return { status: 'refused', ...refusal(dry) };
    return { status: 'ready', snapshot: snap.snapshot, composed: dry.html };
  }, [slot, candidateDraftId, loaded, tenantId, namespace, pageHtml]);

  const doSwap = () => {
    setActionError(null);
    if (swapCheck.status !== 'ready') return;
    const { snapshot } = swapCheck;
    const result = applyBandSnapshot({
      doc: pageHtml,
      bandId: snapshot.bandId,
      tenantId,
      snapshot,
    });
    if (!result.ok) {
      const { reason, detail } = refusal(result);
      setActionError(detail ? `${reason} — ${detail}` : reason);
      return;
    }
    setSwapped({
      html: result.html,
      warnings: swapWarnings(snapshot.html, result.html),
    });
  };

  const pin = async (draftId: string) => {
    setActionError(null);
    const draft = drafts.find((d) => d.id === draftId);
    try {
      await sectionFavoritesService.create({
        name: `${slot} · ${draft ? draftLabel(draft) : draftId}`,
        source_draft_id: draftId,
        section_instance_id: slot as string,
        semantic_role: slot as string,
      });
      await favorites.refetch();
    } catch (err: unknown) {
      setActionError(`pin failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const unpin = async (favoriteId: string) => {
    setActionError(null);
    try {
      await sectionFavoritesService.remove(favoriteId);
      await favorites.refetch();
    } catch (err: unknown) {
      setActionError(`unpin failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const commitRename = async () => {
    if (!renaming) return;
    setActionError(null);
    try {
      await sectionFavoritesService.rename(renaming.id, renaming.value);
      setRenaming(null);
      await favorites.refetch();
    } catch (err: unknown) {
      setActionError(`rename failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div className="space-y-3">
      {actionError && (
        <p className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {actionError}
        </p>
      )}

      {history.isError && (
        <p className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          This client's earlier runs could not be loaded, so there are no candidates to offer:{' '}
          {(history.error as Error)?.message}
        </p>
      )}

      <ul className="space-y-1">
        {spine.map((s) => {
          const open = slot === s.bandId;
          return (
            <li key={s.bandId} className="rounded border border-border">
              <div className="flex flex-wrap items-center gap-2 px-2 py-1.5 text-xs">
                <button
                  type="button"
                  disabled={!s.offerable}
                  onClick={() => chooseSlot(open ? null : s.bandId)}
                  className={`w-36 rounded px-2 py-1 text-left font-medium ${
                    open
                      ? 'bg-teal-dark text-white'
                      : s.offerable
                        ? 'border border-border hover:bg-teal-light'
                        : 'text-muted-foreground'
                  }`}
                >
                  {s.bandId}
                </button>
                <span className="rounded border border-border px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                  {s.verdict}
                </span>
                {s.state !== 'present' && (
                  <span className="text-muted-foreground">
                    not on the page — {s.reason}
                  </span>
                )}
                {s.offerable && !open && (
                  <span className="text-muted-foreground">swap this band</span>
                )}
              </div>

              {open && candidateList && (
                <div className="border-t border-border bg-muted/30 p-3">
                  <p className="mb-2 text-xs font-semibold">
                    {s.bandId} from this client's other runs
                  </p>
                  {candidateList.otherClientPins > 0 && (
                    <p className="mb-2 text-xs text-muted-foreground">
                      {candidateList.otherClientPins} pinned {s.bandId} band
                      {candidateList.otherClientPins === 1 ? '' : 's'} from a different client
                      {candidateList.otherClientPins === 1 ? ' is' : ' are'} not shown: a band carries its own
                      client's copy, so it cannot be used here.
                    </p>
                  )}
                  <ul className="space-y-1">
                    {candidateList.candidates.map((c) => (
                      <li key={c.draftId} className="flex flex-wrap items-center gap-2 text-xs">
                        <button
                          type="button"
                          disabled={c.state !== 'ready'}
                          onClick={() => chooseCandidate(c.draftId)}
                          className={`rounded px-2 py-1 ${
                            candidateDraftId === c.draftId
                              ? 'bg-teal-dark text-white'
                              : c.state === 'ready'
                                ? 'border border-border hover:bg-teal-light'
                                : 'text-muted-foreground'
                          }`}
                        >
                          {c.state === 'loading' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'compare'}
                        </button>
                        {c.pin && <Pin className="h-3 w-3 text-coral-dark" />}
                        {renaming && c.pin && renaming.id === c.pin.favoriteId ? (
                          <>
                            <input
                              className="h-6 w-56 rounded border border-border px-2 text-xs"
                              value={renaming.value}
                              onChange={(e) => setRenaming({ id: renaming.id, value: e.target.value })}
                            />
                            <button
                              type="button"
                              onClick={commitRename}
                              className="rounded border border-border px-2 py-0.5"
                            >
                              <Check className="h-3 w-3" />
                            </button>
                          </>
                        ) : (
                          <span className={c.state === 'ready' ? '' : 'text-muted-foreground'}>
                            {c.pin ? `${c.pin.name} — ` : ''}
                            {c.label}
                          </span>
                        )}
                        {c.state === 'unresolved' && (
                          <span className="min-w-0 break-words text-destructive">
                            {c.reason}
                            {c.detail && <span className="block text-muted-foreground">{c.detail}</span>}
                          </span>
                        )}
                        {c.pin ? (
                          <>
                            <button
                              type="button"
                              onClick={() => setRenaming({ id: c.pin!.favoriteId, value: c.pin!.name })}
                              className="text-muted-foreground underline"
                            >
                              rename
                            </button>
                            <button
                              type="button"
                              onClick={() => unpin(c.pin!.favoriteId)}
                              title="unpin"
                              className="text-muted-foreground"
                            >
                              <PinOff className="h-3 w-3" />
                            </button>
                          </>
                        ) : (
                          c.state === 'ready' && (
                            <button
                              type="button"
                              onClick={() => pin(c.draftId)}
                              title="pin this one"
                              className="text-muted-foreground"
                            >
                              <Pin className="h-3 w-3" />
                            </button>
                          )
                        )}
                      </li>
                    ))}
                  </ul>
                  {others.length > shownCount && (
                    <button
                      type="button"
                      onClick={() => setShownCount(shownCount + CANDIDATE_PAGE)}
                      className="mt-2 inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:bg-teal-light"
                    >
                      <RefreshCw className="h-3 w-3" />
                      load {Math.min(CANDIDATE_PAGE, others.length - shownCount)} older runs
                    </button>
                  )}

                  {swapCheck.status === 'loading' && (
                    <p className="mt-3 text-xs text-muted-foreground">this run has not finished loading</p>
                  )}

                  {/* A refused swap is not a faithful preview: do not draw the
                      two stripped panes or label anything "on the page now"
                      Show the reason; nothing is saved. */}
                  {swapCheck.status === 'refused' && (
                    <div className="mt-3 rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                      {/* A full-sentence reason (the typography refusal) is shown verbatim. */}
                      <p>{/^[A-Z]/.test(swapCheck.reason) ? swapCheck.reason : `This band cannot be swapped in: ${swapCheck.reason}`}</p>
                      {swapCheck.detail && <p className="mt-1 text-muted-foreground">{swapCheck.detail}</p>}
                      {!swapCheck.reason.includes('Nothing has been saved') && (
                        <p className="mt-1 text-muted-foreground">Nothing has been saved.</p>
                      )}
                    </div>
                  )}

                  {swapCheck.status === 'ready' && (
                    <div className="mt-3">
                      {/* Full document context on both sides: the page as it is,
                          and the page as it would be with this band swapped in.
                          No stripped HTML+CSS pane, so nothing is labelled "on
                          the page now" while missing the page's fonts/wrappers. */}
                      <div className="flex gap-3">
                        <FullPreview title="on the page now" html={pageHtml} />
                        <FullPreview title="with the candidate swapped in" html={swapCheck.composed} />
                      </div>
                      <button
                        type="button"
                        onClick={doSwap}
                        className="mt-2 rounded bg-teal-dark px-3 py-1.5 text-xs text-white"
                      >
                        Swap this band in
                      </button>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {strays.length > 0 && (
        <p className="text-xs text-coral-dark">
          This run also carries {strays.join(', ')}, which the spine does not name. Not offered.
        </p>
      )}

      {swapped && (
        <div className="rounded border border-border p-3">
          <p className="mb-2 text-xs font-semibold">The page with the band swapped in</p>
          {swapped.warnings.map((w) => (
            <p
              key={w}
              className="mb-2 flex items-center gap-2 rounded border border-coral bg-coral-light px-3 py-2 text-xs text-coral-dark"
            >
              <AlertTriangle className="h-4 w-4" /> {w}
            </p>
          ))}
          <iframe
            title="the swapped page"
            sandbox="allow-scripts"
            srcDoc={toPreviewDocument(withPlaceholderHostedForm(swapped.html))}
            className="h-[640px] w-full rounded border bg-white"
          />
          <p className="mt-2 text-[11px] text-muted-foreground">
            Nothing is saved. This is the page as it would be; writing it back to the run is not in this build.
          </p>
        </div>
      )}
    </div>
  );
}
